import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Tooltip,
  Grid,
  Avatar,
} from '@mui/material';
import {
  Search,
  Delete,
  Refresh,
  Business,
} from '@mui/icons-material';
import { getMerchants, deleteMerchant } from '../services/merchants';

const Merchants = () => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    setLoading(true);
    try {
      const response = await getMerchants();
      setMerchants(response.data);
    } catch (err) {
      setError('Failed to fetch merchants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const filteredMerchants = merchants.filter((merchant) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      merchant.number?.toLowerCase().includes(searchLower) ||
      merchant.Role?.name?.toLowerCase().includes(searchLower)
    );
  });

  const handleDeleteClick = (merchant) => {
    setSelectedMerchant(merchant);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteMerchant(selectedMerchant.id);
      setSuccess('Merchant deleted successfully!');
      setDeleteDialogOpen(false);
      fetchMerchants();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete merchant');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            Merchants Management
          </Typography>
        <Typography variant="body1" sx={{ color: '#6b7280' }}>
          Manage merchant accounts and their shops
        </Typography>
      </Box>

      {/* Action Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search merchants..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(0,0,0,0.02)',
                  '&:hover fieldset': {
                    borderColor: '#4f46e5',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchMerchants}
                sx={{
                  color: '#6b7280',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 2,
                  '&:hover': { color: '#4f46e5', borderColor: '#4f46e5' },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Success/Error Messages */}
      {success && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
          }}
        >
          <Typography sx={{ color: '#16a34a' }}>{success}</Typography>
        </Paper>
      )}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          <Typography sx={{ color: '#dc2626' }}>{error}</Typography>
        </Paper>
      )}

      {/* Merchants Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 6,
            }}
          >
            <CircularProgress sx={{ color: '#4f46e5' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background: 'rgba(79, 70, 229, 0.05)',
                      '& th': {
                        color: '#374151',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <TableCell>ID</TableCell>
                    <TableCell>Merchant</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMerchants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Business sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
                            No merchants found
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {searchTerm
                              ? 'Try adjusting your search criteria'
                              : 'Get started by adding a new merchant'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMerchants
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((merchant, index) => (
                        <TableRow
                          key={merchant.id}
                          sx={{
                            '&:hover': {
                              background: 'rgba(0,0,0,0.02)',
                            },
                            '& td': {
                              borderBottom: '1px solid rgba(0,0,0,0.05)',
                              py: 2,
                              color: '#374151',
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              #{merchant.id || index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                                }}
                              >
                                <Business sx={{ fontSize: 20 }} />
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {merchant.number || 'N/A'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Business sx={{ fontSize: 12 }} /> {merchant.Role?.name || 'merchant'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography variant="body2">
                                {merchant.number}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {merchant.createdAt
                                ? new Date(merchant.createdAt).toLocaleDateString()
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(merchant)}
                                sx={{
                                  color: '#6b7280',
                                  '&:hover': { color: '#dc2626' },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredMerchants.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                color: '#6b7280',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                '& .MuiTablePagination-select': {
                  color: '#374151',
                },
                '& .MuiTablePagination-selectIcon': {
                  color: '#6b7280',
                },
                '& .MuiTablePagination-actions button': {
                  color: '#6b7280',
                  '&:hover': {
                    color: '#4f46e5',
                    background: 'rgba(79, 70, 229, 0.08)',
                  },
                },
              }}
            />
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: '#dc2626',
          }}
        >
          <Delete />
          Delete Merchant
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#6b7280' }}>
            Are you sure you want to delete <strong>{selectedMerchant?.number}</strong>?
            This will also delete all associated shops and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#6b7280',
              '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.4)',
              },
            }}
          >
            Delete Merchant
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Merchants;

